export const projectsData = [
  {
    id: 'van-allen-ml',
    title: 'Hiss/Chorus Wave Classification',
    category: 'MACHINE LEARNING // SPACE PHYSICS',
    description:
      'Unsupervised ML approach to separating whistler-mode hiss and chorus plasma waves without using density as an input, enabling classification in soft plasmapause regions where traditional methods fail.',
    longDescription:
      'Whistler-mode chorus and hiss plasma waves play a critical role in regulating radiation belt electron populations in Earth\'s inner magnetosphere\u2014chorus drives electron acceleration while hiss drives electron losses. Traditional methods for separating these wave types rely on sharp density gradients at the plasmapause, but this approach excludes roughly half of all observation times where the plasmapause gradient is shallow. This means a huge amount of data is thrown out, potentially skewing the empirical wave models used as inputs to radiation belt simulations.\n\nThis project develops an unsupervised method to classify hiss and chorus without using plasma density as an input. Instead, the approach leverages differences in how wave power is distributed across frequencies between the two wave modes. Spectral power distribution functions are quantified using a vector of key parameters, then passed to a self-organizing map (SOM) clustering algorithm. The model is first validated against well-separated chorus and hiss near sharp plasmapause gradients, then applied to the previously inaccessible regions of shallow gradient where the two wave modes mix.',
    tags: ['Python', 'PySPEDAS', 'SOM', 'Unsupervised ML'],
    techStack: ['Python', 'PySPEDAS', 'scikit-learn', 'NumPy', 'Pandas', 'Matplotlib', 'Jupyter', 'SciPy'],
    icon: 'Satellite',
    gradient: 'from-cyber-magenta/20 to-cyber-purple/10',
    cleanGradient: 'from-indigo-50 to-purple-50',
    borderColor: '#ff00ff',
    cleanBorderColor: '#6366f1',
    status: 'ACTIVE',
    timeline: 'Feb 2025 - Present',
    team: 'Research Lab',
    github: null,
    demo: null,
    features: [
      'Density-independent classification of hiss and chorus plasma waves',
      'Self-organizing map (SOM) clustering on spectral power distribution parameters',
      'Enables analysis of soft plasmapause regions excluded by traditional methods',
      'Recovers ~50% of observation time previously discarded due to shallow density gradients',
      'Validated against well-separated wave instances near sharp plasmapause boundaries',
      'Processes 7+ years of Van Allen Probe EMFISIS data',
      'Results presented at GEM and AGU conferences',
    ],
    impact: 'By removing the dependence on sharp density gradients, this method unlocks roughly half of all observation times that were previously excluded from wave models. This provides a more complete picture of hiss and chorus wave properties across the inner magnetosphere, leading to more accurate inputs for radiation belt simulations that are critical for predicting space weather impacts on satellites and astronauts.',
    challenges: 'The central challenge was finding a way to separate two wave modes that are traditionally defined by their relationship to plasma density\u2014without using density itself. The approach required identifying alternative spectral signatures that reliably distinguish hiss from chorus based on differences in their wave power distributions. Additionally, validating the unsupervised results against known classifications in well-separated regions was critical before trusting the model in ambiguous mixing regions.',
    figures: [
      { type: 'video', src: '/images/van-allen/traversal.mp4', caption: 'Traversal algorithm in action, showing the wave-finding method scanning through Van Allen Probe EMFISIS data to identify and classify plasma wave events in real time.' },
      { src: '/images/van-allen/threepanel.png', caption: 'Model classification vs. traditional density-based sorting. In low-density regions, the traditional sort misclassifies hiss as chorus, while the SOM classifier correctly identifies hiss, demonstrating improved accuracy where conventional methods fail.', size: 'medium' },
      { src: '/images/van-allen/hiss_density.png', caption: 'Hiss wave detection density in MLT/L space from the SOM classifier, showing spatial distributions consistent with established hiss occurrence patterns.', group: 'density-comparison', groupPosition: 'top-left' },
      { src: '/images/van-allen/chorus_density.png', caption: 'Chorus wave detection density in MLT/L space from the SOM classifier, showing expected dawn-side enhancement consistent with known chorus behavior.', group: 'density-comparison', groupPosition: 'top-right' },
      { src: '/images/van-allen/previous_data.png', caption: 'Expected hiss and chorus spatial distributions from prior literature. Our SOM-derived distributions (above) are consistent with these established patterns, validating the density-independent approach.', group: 'density-comparison', groupPosition: 'bottom' },
    ],
    supplemental: [
      { type: 'pdf', src: '/images/van-allen/gem_slides.pdf', label: 'GEM Presentation Slides', description: 'Slides from the Geospace Environmental Modeling (GEM) workshop presentation' },
      { type: 'image', src: '/images/van-allen/agu_poster.png', label: 'AGU Poster', description: 'Research poster presented at the American Geophysical Union (AGU) conference' },
    ],
  },
  {
    id: 'cheatgrass-cnn',
    title: 'Cheatgrass Classification Model',
    category: 'DEEP LEARNING // COMPUTER VISION',
    description:
      'Convolutional neural network using PyTorch to automatically identify invasive cheatgrass from native plant species, achieving 90%+ classification accuracy. Optimized for field deployment.',
    longDescription:
      'Built a deep learning computer vision system using PyTorch to automatically distinguish invasive cheatgrass (Bromus tectorum) from native plant species in field imagery. The convolutional neural network achieves over 90% classification accuracy and has been optimized through extensive hyperparameter tuning and architecture experimentation for practical deployment in ecological field surveys.',
    tags: ['PyTorch', 'Deep Learning', 'Computer Vision', 'CNN'],
    techStack: ['Python', 'PyTorch', 'torchvision', 'OpenCV', 'Pillow', 'NumPy', 'Matplotlib', 'CUDA'],
    icon: 'Brain',
    gradient: 'from-cyber-purple/20 to-cyber-pink/10',
    cleanGradient: 'from-purple-50 to-pink-50',
    borderColor: '#bf00ff',
    cleanBorderColor: '#8b5cf6',
    status: 'COMPLETE',
    wip: true,
    timeline: 'Fall 2025',
    team: 'Solo',
    github: null,
    demo: null,
    features: [
      'Custom CNN architecture for plant species classification',
      '90%+ accuracy distinguishing cheatgrass from native species',
      'Data augmentation pipeline for limited field imagery datasets',
      'Hyperparameter optimization through systematic experimentation',
      'Model compression for viable field deployment on mobile devices',
      'Transfer learning from pre-trained image classification networks',
    ],
    impact: 'Enables rapid, automated identification of invasive cheatgrass during ecological surveys, supporting conservation efforts across the western United States. The model can be deployed on mobile devices for real-time field identification.',
    challenges: 'Primary challenges included building a robust training dataset from field photographs with varying lighting and angles, preventing overfitting on a relatively small dataset, and optimizing the model architecture for deployment on resource-constrained devices.',
    figures: [
      { caption: 'CNN architecture diagram and layer configuration' },
      { caption: 'Training/validation accuracy curves over epochs' },
    ],
  },
  {
    id: 'cheatmaps',
    title: 'CheatMaps',
    category: 'FULL-STACK // GEOSPATIAL',
    description:
      'React-based web application for the Bureau of Land Management to track and map invasive cheatgrass populations in Gunnison Valley. Features geospatial data collection and real-time monitoring.',
    longDescription:
      'A comprehensive web application developed as a senior capstone project in collaboration with the Bureau of Land Management (BLM) for Gunnison Valley. CheatMaps enables field workers to collect, visualize, and analyze geospatial data on invasive cheatgrass populations. The platform integrates machine learning for automated cheatgrass detection and provides real-time monitoring dashboards for land management decisions.',
    tags: ['React', 'Geospatial', 'Full-Stack', 'ML Integration'],
    techStack: ['React', 'Node.js', 'PostgreSQL', 'PostGIS', 'Leaflet', 'Python', 'Flask', 'AWS'],
    icon: 'Map',
    gradient: 'from-cyber-pink/20 to-cyber-blue/10',
    cleanGradient: 'from-pink-50 to-cyan-50',
    borderColor: '#ff2d95',
    cleanBorderColor: '#ec4899',
    status: 'ACTIVE',
    wip: true,
    timeline: 'Aug 2025 - Present',
    team: '7 Members',
    github: null,
    demo: null,
    features: [
      'Interactive geospatial map for cheatgrass population tracking',
      'Field data collection with GPS-tagged observations',
      'Real-time monitoring dashboard for land management teams',
      'ML-powered automated cheatgrass detection from field photos',
      'Historical data analysis and population trend visualization',
      'Multi-user authentication and role-based access control',
      'Export tools for BLM reporting requirements',
    ],
    impact: 'Provides the Bureau of Land Management with a modern, data-driven tool for managing invasive species across Gunnison Valley. The platform replaces manual paper-based survey methods and enables more efficient allocation of conservation resources.',
    challenges: 'Coordinating a 7-member development team across frontend, backend, and ML components while meeting BLM requirements. Building a reliable offline-capable data collection system for field use in areas with limited connectivity.',
    figures: [
      { caption: 'CheatMaps dashboard with population density heatmap' },
      { caption: 'Mobile field data collection interface' },
      { caption: 'Historical trend analysis for monitored regions' },
    ],
  },
  {
    id: 'bookbag',
    title: 'BookBag - Literary Companion',
    category: 'FULL-STACK // WEB APPLICATION',
    description:
      'Full-stack web application with PostgreSQL database, secure user authentication, and integration with Google Books API and OpenLibrary for comprehensive book data aggregation.',
    longDescription:
      'BookBag is a full-stack literary companion application that helps readers discover, track, and organize their reading. Built collaboratively with a 7-member team, it features a PostgreSQL database backend with secure user authentication and integrates multiple external APIs including Google Books and OpenLibrary for comprehensive book search and metadata aggregation.',
    tags: ['Full-Stack', 'PostgreSQL', 'API Integration', 'Auth'],
    techStack: ['Node.js', 'Express', 'PostgreSQL', 'EJS', 'Google Books API', 'OpenLibrary API', 'bcrypt', 'CSS'],
    icon: 'BookOpen',
    gradient: 'from-cyber-blue/20 to-cyber-magenta/10',
    cleanGradient: 'from-cyan-50 to-indigo-50',
    borderColor: '#00d4ff',
    cleanBorderColor: '#06b6d4',
    status: 'COMPLETE',
    wip: true,
    timeline: 'Spring 2025',
    team: '7 Members',
    github: null,
    demo: null,
    features: [
      'Comprehensive book search powered by multiple APIs',
      'Personal reading lists with progress tracking',
      'Secure user authentication with hashed passwords',
      'Book recommendations based on reading history',
      'Aggregate metadata from Google Books and OpenLibrary',
      'Responsive design for mobile and desktop reading',
    ],
    impact: 'Provides readers with a unified platform to discover and track books, aggregating data from multiple sources for the most comprehensive book information available in a single interface.',
    challenges: 'Reconciling inconsistent data across multiple book APIs, implementing secure authentication from scratch, and coordinating a 7-member team with varying experience levels across the full stack.',
    figures: [
      { caption: 'BookBag search interface with aggregated results' },
      { caption: 'Personal reading list and progress tracking view' },
    ],
  },
];
